import React from 'react';
import { Column } from 'react-table';
import { message } from 'antd';
import Modal from '../Modal';
import Table from '../table/Table';
import TableWithSummary from '../table/TableWithSummary';
import { useSelectList } from '../../hooks/common/useSelectList';
import { useTableList } from '../../hooks/common/useTableList';
import { ColumnUtilType, Option, ReactTableOption, SearchOption, TableListData } from '../../types/common/table';
import {
  changeReactTableSortToTableSort,
  changeTableSortToReactTableSort,
  reactTableSimpleColumns,
} from '../../lib/utils/tableUtils';
import { TableTitleStyle, TableWrapperStyle } from './style';

export interface SearchListModalProps<T, F> {
  queryKey: string[];
  isOpen: boolean;
  onOk: (selectedList: T[]) => void;
  onClose: () => void;
  columns: (utils: ColumnUtilType<T, F>) => Column<T>[];
  value?: T[];
  fetcher: (option: SearchOption<F>) => Promise<TableListData<T>>;
  canMultiple?: boolean;
  initialOption?: Option<F>;
}

/**
 * api를 통해 데이터를 불러와 하단에 위치한 테이블에 노출시키고
 * 하단 테이블에서 아이템을 체크하면 상단 테이블에 선택된 아이템이 보이는 컴포넌트
 * 확인을 누르면 선택된 아이템이 리스트 형태로 등록
 * */
const SearchListModal = <T extends { id: number }, F extends Record<string, unknown>>(
  props: SearchListModalProps<T, F>,
): JSX.Element => {
  const { queryKey, fetcher, value, isOpen, onOk, onClose, columns, canMultiple, initialOption = {} } = props;
  const { totalCount, list, pagination, sortOption, filterOption, refetchList } = useTableList({
    fetcher,
    initialOption,
    queryKey,
  });

  const { selectedList, changeSelectList } = useSelectList({
    list,
    canMultiple,
    value,
  });

  const checkedIdList = selectedList.map((item) => item.id);
  const handleOk = (): void => {
    if (!selectedList.length) {
      message.error('아이템을 선택해주세요');
      return;
    }
    onOk(selectedList);
  };

  return (
    <Modal isOpen={isOpen} width={1500} height={700} isFooter={true} onOk={handleOk} onClose={onClose}>
      <TableWrapperStyle data-testid="selected-list-table">
        <TableTitleStyle>선택된 아이템</TableTitleStyle>
        <Table
          data={selectedList}
          emptyText="선택된 아이템이 없습니다"
          checkedIdList={checkedIdList}
          onCheck={changeSelectList}
          filterable={false}
          defaultPageSize={1}
          isUsedAllCheck={false}
          isUsedCheckbox={true}
          columns={(utils: ColumnUtilType<T, F>): Column<T>[] =>
            canMultiple ? columns({ ...utils }) : reactTableSimpleColumns(columns(utils))
          }
        />
      </TableWrapperStyle>

      <TableWrapperStyle data-testid="item-list-table">
        <TableTitleStyle>아이템 리스트</TableTitleStyle>
        <TableWithSummary
          columns={(utils: ColumnUtilType<T, F>): Column<T>[] => columns({ ...utils, sortable: true })}
          totalCnt={totalCount}
          data={list}
          emptyText="리스트가 없습니다"
          maxHeight={500}
          showPagination
          getList={(option: ReactTableOption<F>): void =>
            refetchList({
              ...option,
              sortOption: option.sortOption && changeReactTableSortToTableSort(option.sortOption),
            })
          }
          isUsedAllCheck={false}
          isUsedCheckbox={true}
          checkedIdList={checkedIdList}
          onCheck={changeSelectList}
          filterOption={filterOption}
          pagination={{
            ...pagination,
            pages: totalCount ? Math.ceil(totalCount / pagination.pageSize) : 1,
          }}
          sortOption={sortOption && changeTableSortToReactTableSort(sortOption)}
          initialOption={
            initialOption && {
              filterOption: initialOption.filterOption || {},
              pagination: initialOption.pagination || {
                pageSize: 100,
                pageNum: 0,
                pages: totalCount ? Math.ceil(totalCount / 100) : 1,
              },
              sortOption: initialOption.sortOption && changeTableSortToReactTableSort(initialOption.sortOption),
            }
          }
        />
      </TableWrapperStyle>
    </Modal>
  );
};

export default SearchListModal;
